import { faEnvelope, faPhone } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import constants from "../constants/constants";
import { useState, useEffect } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import { useUserParams } from "../hooks/useUserParams";

export default function Contact() {
  const [popup, setPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage
  } = useUserParams();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Form validation function
  const validateForm = () => {
    const errors = {};
    
    // Check if Name is empty
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }
    
    // Check if Email is empty and valid format
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    
    // Check if Message is empty
    if (!formData.message.trim()) {
      errors.message = "Message is required";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  async function submitForm(event) {
    event.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    const json = JSON.stringify({
      ...formData,
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Contact Form Message",
    });

    const res = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: json,
    })
      .then((res) => res.json())
      .catch((err) => {
        console.log(err);
      });

    if (res?.success) {
      console.log("Success", res);
      setPopup({
        subject: "Thanks for your message!",
        message: "We will get back to you as soon as possible",
      });
      // Clear form after successful submission
      setFormData({
        name: "",
        email: "",
        message: "",
      });
      setFormErrors({});
    } else {
      setPopup({
        subject: "We encountered an error in sending this message",
        message: "Please try again later",
      });
    }
    
    setIsSubmitting(false);
  }

  const [windowSize, setWindowSize] = useState(window.innerWidth);

  const onWindowResize = () => {
    setWindowSize(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener("resize", onWindowResize);
    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
  }, []);


  const seoConfig = pageSEO.contact();

  return (
    <>
      <SEO {...seoConfig} />
      <div className="w-full bg-light-grey flex flex-wrap gap-x-16 gap-y-10 justify-center py-16 px-7 min-h-[70vh]">
        <div>
          <h4 className="font-mont font-semibold text-black text-lg min-w-[20vw]">
            Questions? I'm Here To Help
          </h4>
          <form onSubmit={submitForm} className="mt-6 flex flex-col">
            <div className="form-item">
              <label className="form-label">Name*</label>
              <input
                className={`form-input ${formErrors.name ? 'border-red-500' : ''}`}
                name="name"
                placeholder="Full Name"
                value={formData.name}
                onChange={handleInputChange}
              />
              {formErrors.name && (
                <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">Email*</label>
              <input
                className={`form-input ${formErrors.email ? 'border-red-500' : ''}`}
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleInputChange}
              />
              {formErrors.email && (
                <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">Message*</label>
              <textarea
                className={`form-input resize-none h-32 ${formErrors.message ? 'border-red-500' : ''}`}
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleInputChange}
              />
              {formErrors.message && (
                <p className="text-red-500 text-sm mt-1">{formErrors.message}</p>
              )}
            </div>
            <button 
              type="submit" 
              className="btn-primary rounded-none self-start px-10 "
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>
        <div>
          <iframe
            class="contact-map"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2885.1670922064877!2d-79.3108446!3d43.686289499999994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d4cc14ec855631%3A0xb3d0feb68a2d0ed3!2s85%20Cedarvale%20Ave%2C%20Toronto%2C%20ON%20M4C%204J7!5e0!3m2!1sen!2sca!4v1730912488107!5m2!1sen!2sca"
            height="365"
            width={`${windowSize > 900 ? "500" : "300"}`}
            style={{ border: 0 }}
            allowfullscreen=""
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          ></iframe>

          <div className="flex flex-col gap-y-2 text-black mt-6">
            <h2 className="text-black text-2xl">{getCurrentAgentName()}</h2>
            <p className="italic">
              {getCurrentAgentTitle()}, {getCurrentBrokerage()}
            </p>
            <p>
              <FontAwesomeIcon icon={faPhone} className="text-blue-800 fa-lg" />{" "}
              {getCurrentAgentNumber()}{" "}
            </p>
            <p>
              <FontAwesomeIcon
                icon={faEnvelope}
                className="text-blue-800 fa-lg"
              />{" "}
              {getCurrentAgentEmail()}{" "}
            </p>
          </div>
        </div>
      </div>
      {popup && (
        <>
          <div className="backdrop" />
          <div className="popup">
            <h5 className="font-bold text-black text-lg">{popup.subject}</h5>
            <p className="font-semibold text-med-grey tracking-wide mb-3">
              {popup.message}
            </p>
            <button onClick={() => setPopup(false)} className="btn-primary">
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
